/* eslint-disable prettier/prettier */
/* eslint-disable react-native/no-inline-styles */
import React, { useState, useEffect, useRef } from 'react';
import { View, Text, ScrollView, Image, Linking, TouchableOpacity } from 'react-native';
import QRCode from 'react-native-qrcode-svg';
import DeviceInfo from 'react-native-device-info';
import i18n from 'i18n-js';
import { connect } from 'react-redux';
import moment from 'moment';
import { Camera, getCameraDevice } from 'react-native-vision-camera';

import { getAuthInfo, authenticateWalboardDevice, submitActivationDetails } from '@reducers/device';

import ScanWrapper from '@assets/img/SettingsMobileQr.png';

import { isIOS, isPhone, isWeb, Alert, ENVIRONMENTS, getWebUrl, getSecureValue, getSelectedCameraDevice } from '@helpers';

import { SmallLoading } from '@components/loading';

import Android from '@assets/download/android.svg';
import Apple from '@assets/download/apple.svg';
import TV from '@assets/download/tv.svg';
import Web from '@assets/download/web.svg';
import AndroidW from '@assets/download/androidWhite.svg';
import AppleW from '@assets/download/appleWhite.svg';
import TVW from '@assets/download/tvWhite.svg';
import WebW from '@assets/download/webWhite.svg';

import ColorWrapper from '@styles/colors';
import Button from '@styles/buttons';
import { font } from '@styles/fonts';

import { Element, ElementBox, GridBox, scale, width, scaleFactor, height, notchTop, env } from '@components/frame';
import BackButton from '@components/common/backButton';
import AnimatedHeader from '@components/common/AnimatedHeader';
import ActivateDevice from '@components/settings/components/common/activateDevice';

export const ScanQRCode = ({ vertical, C, isCameraWorking, setIsCameraWorking, authenticateWalboardDevice, submitActivationDetails, device }) => {

  const scanned = useRef(false);
  const [cameraDevice, setDevice] = useState(null);

  const scanedQR = () => {
    setIsCameraWorking(false);
    scanned.current = false;
  };

  const handleBarCodeScanned = async (c) => {
    const data = c.value;

    scanned.current = true;

    let json = {};

    const error = () => {
      scanedQR();
      Alert({ title: i18n.t('Wrong QR code') });
    };

    try {
      json = JSON.parse(data);
    } catch {
      return error();
    }

    const { deviceType, code, expirationDateTime, appHubID } = json;
    if (appHubID) {
      await submitActivationDetails({
        deviceCode: device.authInfo.deviceCode,
        authenticationCode: device.authInfo.authenticationCode,
        deviceConnectionId: appHubID,
      });
      scanedQR();
      return;
    }
    if (!expirationDateTime) {
      return error();
    }
    const exDate = moment.utc(expirationDateTime).diff(moment(), 'seconds');
    if (exDate <= 1) {
      return error();
    }
    await authenticateWalboardDevice({ deviceType, authenticationCode: code, expirationDateTime });

    scanedQR();
  };

  const validateCameraPermission = async (status) => {
    switch (status) {
      case 'not-determined': {
        const newCameraPermission = await Camera.requestCameraPermission();
        return await validateCameraPermission(newCameraPermission);
      }
      case 'granted': {
        const selectedDevice = await getSelectedCameraDevice();

        setDevice(selectedDevice);
        setIsCameraWorking(true);
        break;
      }
      default: {
        setIsCameraWorking(false);
        Linking.openSettings();
      }
    }
  };

  useEffect(() => {
    (async () => {
      const status = Camera.getCameraPermissionStatus();
      await validateCameraPermission(status);
      setLocalLoading(false);
    })();
  }, []);

  const askAgain = async () => {
    const newCameraPermission = await Camera.requestCameraPermission();
    return await validateCameraPermission(newCameraPermission);
  };

  const w = isPhone ? width : scale(360);

  return (
    <View
      style={{
        width: vertical,
        marginTop: notchTop === 0 ? scale(-30) : 0,
      }}
    >
      {!isCameraWorking && (
        <Element
          {...{
            config: {
              horizontalCenter: 0,
              verticalCenter: -86.5,
              parentWidth: vertical,
              width: 324,
              height: 65,
            },
            skipPriority: true,
            configMobile: {
              paddingLeft: scale(18),
              paddingRight: scale(18),
              position: {
                top: 29.5,
              },
              width: '100%',
            },
          }}
        >
          {isPhone ? (
            <Button {...{ variant: 'Dialog L', type: 'Primary', onPress: askAgain }}>{i18n.t('Scan QR code with this device')}</Button>
          ) : (
            <TouchableOpacity activeOpacity={1} onPress={askAgain}>
              <View
                style={{
                  backgroundColor: C('Button/Light/Primary'),
                  paddingTop: scale(isIOS ? 3 : 1.5),
                  width: scale(324),
                  height: scale(65),
                  paddingLeft: scale(40),
                  paddingRight: scale(40),
                  alignContent: 'center',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: scale(10),
                }}
              >
                <Text style={[font['Button 1'], { color: C('Label/Light/Inverted') }]}>{i18n.t('Scan QR code with this device')}</Text>
              </View>
            </TouchableOpacity>
          )}
        </Element>
      )}
      <View>
        {(isCameraWorking || !isPhone) && (
          <Element
            {...{
              config: {
                position: {
                  top: 118,
                },
                horizontalCenter: 0,
                parentWidth: vertical,
                width: 324,
              },
              configMobile: {
                position: {
                  top: 29,
                },
                width: 200,
              },
            }}
          >
            <Text
              style={[
                {
                  color: C('Label / Light / Primary'),
                  textAlign: 'center',
                },
                font['Title 2'],
              ]}
            >
              {i18n.t('Scan QR code')}
            </Text>
          </Element>
        )}
        {isCameraWorking && (
          <Element
            {...{
              config: {
                position: {
                  top: 202,
                },
                width: 360,
                height: 360,
                horizontalCenter: 0,
                parentWidth: vertical,
              },
              skipPriority: true,
              configMobile: {
                position: {
                  top: 55,
                },
                width: '100%',
                height: width / scaleFactor,
              },
            }}
          >
            <Image
              style={{
                position: 'absolute',
                zIndex: 10,
                width: w,
                height: w,
              }}
              resizeMode="contain"
              source={ScanWrapper}
            />
            <View style={{ width: w, height: w, overflow: 'hidden' }}>
              <Camera
                device={cameraDevice}
                style={{ width: w, height: w }}
                codeScanner={{
                  codeTypes: ['qr'],
                  onCodeScanned: (codes) => {
                    if (!scanned.current) {
                      handleBarCodeScanned(codes[0]);
                    }
                  },
                }}
                isActive={true}
              />
            </View>
          </Element>
        )}
      </View>
    </View>
  );
};
const AddInfo = (props) => {
  const { C, vertical, device, isCamera, isCameraWorking, setIsCameraWorking, isLoaded } = props;
  const code = device.authInfo && device.authInfo.authenticationCode ? device.authInfo.authenticationCode : '';

  const offset = isCameraWorking && isLoaded ? width : 0;

  const scrollSize = isPhone ? (isCamera && isCameraWorking && isLoaded ? 1100 : 750) : height - scale(200);

  return (
    <View
      style={{
        width: '100%',
        height: '100%',
        marginTop: isPhone ? scale(55) : 0,
      }}
    >
      <ScrollView>
        <View
          style={[
            {
              width: '100%',
              height: scale(scrollSize),
              paddingTop: isPhone ? scale(30) : 0,
              paddingBottom: scale(150),
            },
            !isPhone && { justifyContent: 'space-between', flexDirection: 'row' },
          ]}
        >
          <View
            style={[
              {
                width: vertical,
              },
              isPhone && isCamera && isLoaded && { top: scale(90) + offset },
            ]}
          >
            <Element
              {...{
                config: {
                  position: {
                    top: 118,
                  },
                  horizontalCenter: 0,
                  parentWidth: vertical,
                  width: 324,
                  display: 'flex',
                  opacity: 1,
                },
                configMobile: {
                  display: 'none',
                  opacity: 0,
                },
              }}
            >
              <Text
                style={[
                  {
                    color: C('Label / Light / Primary'),
                    textAlign: 'center',
                  },
                  font['Title 2'],
                ]}
              >
                {i18n.t('Scan QR code')}
              </Text>
              <Text
                style={[
                  {
                    color: C('Label / Light / Primary'),
                    textAlign: 'center',
                  },
                  font['Title 2'],
                ]}
              >
                {i18n.t('on new device')}
              </Text>
            </Element>
            <Element
              {...{
                config: {
                  display: 'none',
                  opacity: 0,
                },
                configMobile: {
                  display: 'flex',
                  opacity: 1,
                  position: {
                    top: isCameraWorking ? 197 : 227,
                  },
                  horizontalCenter: 0,
                  parentWidth: vertical,
                  width: 240,
                },
              }}
            >
              <Text
                style={[
                  {
                    color: C('Label / Light / Primary'),
                    textAlign: 'center',
                  },
                  font['Title 2'],
                ]}
              >
                {i18n.t('Add with QR code')}
              </Text>
            </Element>
            <Element
              {...{
                config: {
                  horizontalCenter: 0,
                  verticalCenter: -32,
                  parentWidth: vertical,
                  width: 240,
                  height: 240,
                  backgroundColor: 'white',
                  padding: scale(5),
                  justifyContent: 'center',
                },
                skipPriority: true,
                configMobile: {
                  horizontalCenter: 0,
                  parentWidth: vertical,
                  width: 240,
                  height: 240,
                  position: {
                    top: isCameraWorking ? 228 : 258,
                  },
                  backgroundColor: 'white',
                  padding: scale(5),
                  justifyContent: 'center',
                },
              }}
            >
              {device.authInfo && device.authInfo.deviceCode ? (
                <QRCode
                  value={`${getWebUrl(env)}/qr?addDevice=${JSON.stringify(device.authInfo)}`}
                  size={scale(230)}
                  backgroundColor={'white'}
                  color={'black'}
                />
              ) : (
                <SmallLoading />
              )}
            </Element>
            <Element
              {...{
                config: {
                  horizontalCenter: 0,
                  verticalCenter: 137.5,
                  parentWidth: vertical,
                  width: 324,
                  height: 77,
                },
                skipPriority: true,
                configMobile: {
                  horizontalCenter: 0,
                  parentWidth: vertical,
                  width: 300,
                  position: {
                    top: isCameraWorking ? 473 : 503,
                  },
                },
              }}
            >
              <Text
                style={[
                  {
                    color: C('Label / Light / Secondary'),
                    textAlign: 'center',
                  },
                  isPhone ? font['Subhead 2'] : font.Body,
                ]}
              >
                {/* Scan this QR code on the Smart Factory app or Wallboard app of the device that you would like to add */}
                {i18n.t('Scan this QR code…')}
              </Text>
            </Element>
          </View>
          {isLoaded && <ScanQRCode {...props} />}
          <View
            style={[
              {
                width: vertical,
              },
              isPhone && isCamera && isLoaded && { top: scale(isCameraWorking ? 70 : 98) + offset },
            ]}
          >
            <Element
              {...{
                config: {
                  position: {
                    top: 118,
                  },
                  horizontalCenter: 0,
                  parentWidth: vertical,
                  width: 324,
                  // height: 77,
                },
                configMobile: {
                  position: {
                    top: 20,
                  },
                  width: 200,
                  // height: 18,
                },
              }}
            >
              <Text
                style={[
                  {
                    color: C('Label / Light / Primary'),
                    textAlign: 'center',
                  },
                  font['Title 2'],
                ]}
              >
                {i18n.t('Add with passcode')}
              </Text>
            </Element>
            <Element
              {...{
                config: {
                  position: {
                    top: 151,
                  },
                  horizontalCenter: 0,
                  parentWidth: vertical,
                  width: 324,
                  height: 77,
                },
                configMobile: {
                  position: {
                    top: 46,
                  },
                  width: 200,
                  height: 34,
                },
              }}
            >
              <Text
                style={[
                  {
                    color: C('Label / Light / Secondary'),
                    textAlign: 'center',
                  },
                  isPhone ? font['Caption 1'] : font['Subhead 1'],
                ]}
              >
                {i18n.t('(only for Wallboard)')}
              </Text>
            </Element>
            <Element
              {...{
                config: {
                  horizontalCenter: 0,
                  verticalCenter: -86.5,
                  parentWidth: vertical,
                  width: scale(isCamera ? 324 : 364),
                  height: 65,
                  justifyContent: 'center',
                  alignItems: 'center',
                  alignContent: 'center',
                },
                skipPriority: true,
                configMobile: {
                  horizontalCenter: 0,
                  parentWidth: vertical,
                  position: {
                    top: isCameraWorking ? 72 : 75,
                  },
                  width: 324,
                  height: 65,
                },
              }}
            >
              <View
                style={[
                  isPhone
                    ? {
                        backgroundColor: C('Background / Light / Field lighter'),
                        paddingLeft: scale(25),
                        paddingRight: scale(25),
                        width: scale(324),
                        height: scale(65),
                      }
                    : {
                        backgroundColor: C('Button / Light / Tertiary'),
                        paddingTop: scale(isIOS ? 3 : 1.5),
                        width: scale(isCamera ? 324 : 364),
                        height: scale(65),
                        paddingLeft: scale(40),
                        paddingRight: scale(40),
                      },
                  {
                    alignContent: 'center',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: scale(10),
                  },
                ]}
              >
                <View
                  style={{
                    width: '100%',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {/* code */}
                  {code.split('').map((item, index) => (
                    <View
                      style={[
                        {
                          marginRight: scale(13),
                        },
                        (index + 1) % 3 === 0 && {
                          marginRight: scale(25),
                        },
                        index === code.split('').length - 1 && {
                          marginRight: 0,
                        },
                      ]}
                      key={`${item}-${index}`}
                    >
                      <Text
                        key={`${item}-${index}`}
                        style={[
                          {
                            color: C('Label / Light / Primary'),
                          },
                          font['Title 1'],
                        ]}
                      >
                        {item}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>
            </Element>
            <Element
              {...{
                config: {
                  horizontalCenter: 0,
                  verticalCenter: -8,
                  parentWidth: vertical,
                  width: 324,
                  height: 56,
                },
                skipPriority: true,
                configMobile: {
                  horizontalCenter: 0,
                  parentWidth: vertical,
                  position: {
                    top: isCameraWorking ? 141 : 144,
                  },
                  width: 324,
                  height: 42,
                },
              }}
            >
              <Text
                style={[
                  {
                    color: C('Label / Light / Secondary'),
                    textAlign: 'center',
                  },
                  isPhone ? font['Subhead 2'] : font.Body,
                ]}
              >
                {i18n.t('Enter this code on the Wallboard app of the device that you would like to add')}
              </Text>
            </Element>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const AddInfoWrapped = (props) => {
  const { checkingCamera, isCamera } = props;

  useEffect(() => {
    if (!isWeb) {
      (async () => {
        const env = getSecureValue('env');
        if (env !== ENVIRONMENTS.PROD) {
          return;
        }
        await checkingCamera();
      })();
    }
  }, []);

  const verticalsLine = isPhone ? 1 : isCamera ? 3 : 2;
  const verticalSpace = isPhone ? 0 : 36;
  const verticalEdge = isPhone ? 0 : 18;

  return (
    <GridBox
      {...{
        headerOffset: false,
        notchOffset: false,
        sliderOffset: false,
        verticalsLine,
        verticalSpace,
        verticalEdge,
      }}
    >
      <AddInfo {...{ ...props }} />
    </GridBox>
  );
};

let secretScreen = 0;

const AddDeviceComponent = (props) => {
  const {
    C,
    getAuthInfo,
    signalR: { appHubID },
    device,
  } = props;

  useEffect(() => {
    (async () => {
      if (appHubID) {
        await getAuthInfo();
      }
    })();
  }, [appHubID]);

  const [isLoaded, setIsLoaded] = useState(false);
  const [isCamera, setIsCamera] = useState(!isWeb);
  const [isCameraWorking, setIsCameraWorking] = useState(false);
  const checkingCamera = async () => {
    const isC = await DeviceInfo.isCameraPresent();
    setIsCamera(isIOS ? true : isC);
    setIsLoaded(true);
  };

  const goToSecret = async () => {
    if (secretScreen === 4) {
      secretScreen = 0;
      await checkingCamera();
    } else {
      secretScreen += 1;
    }
  };

  const HeaderText = () => {
    return (
      <View
        style={{
          bottom: scale(isWeb ? 17 : 10, false),
          width: '100%',
          position: 'absolute',
        }}
      >
        <TouchableOpacity activeOpacity={1} onPress={goToSecret}>
          <Text
            style={[
              font.Headline,
              {
                color: C('Label / Light / Primary'),
                width: '100%',
                textAlign: 'center',
              },
            ]}
          >
            {i18n.t('Add device')}
          </Text>
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <ElementBox>
      {isPhone ? (
        <AnimatedHeader
          {...{
            C,
            animated: false,
            back: true,
            backgroundColor: 'Background/Light/Detail',
            borderBottomWidth: 0,
            TextComponent: HeaderText,
          }}
        />
      ) : (
        <Element
          {...{
            config: {
              position: {
                top: 24,
                left: 0,
              },
              width: 125,
              height: 44,
              justifyContent: 'center',
              zIndex: 1000,
            },
            configMobile: {
              position: {
                top: 20,
                left: 0,
              },
              width: 120,
              height: 42,
            },
          }}
        >
          <BackButton />
        </Element>
      )}
      <Element
        {...{
          config: {
            position: {
              top: 39,
            },
            width: 630,
            horizontalCenter: 9,
            display: 'flex',
            opacity: 1,
          },
          configMobile: {
            display: 'none',
            opacity: 0,
          },
        }}
      >
        <Text
          style={[
            {
              color: C('Label/Light/Primary'),
              textAlign: 'center',
            },
            font['Title 1'],
          ]}
        >
          {i18n.t('How would you like to add a new device?')}
        </Text>
      </Element>
      <AddInfoWrapped {...{ device, ...props, checkingCamera, isLoaded, isCamera, isCameraWorking, setIsCameraWorking }} />
      <Element
        {...{
          config: {
            position: {
              bottom: 0,
            },
            height: 108,
            width: '100%',
            display: 'flex',
            opacity: 1,
          },
          configMobile: {
            display: 'none',
            opacity: 0,
          },
        }}
      >
        {isWeb && (
          <Element
            {...{
              config: {
                position: {
                  top: 0,
                  left: -1350,
                },
                width: 4000,
                height: 38,
                backgroundColor: C('Selected / Light / Secondary'),
              },
            }}
          />
        )}
        <Element
          {...{
            config: {
              position: {
                top: 0,
                right: 0,
                left: 0,
              },
              height: 38,
              backgroundColor: C('Selected / Light / Secondary'),
              justifyContent: 'center',
              alignItems: 'center',
              alignContent: 'center',
            },
          }}
        >
          <Text
            style={[
              {
                textAlign: 'center',
                color: C('Label/Light/Tertiary'),
              },
              font.Headline,
            ]}
          >
            {/* Download our free Apps to see your factory in one view */}
            {i18n.t('Download our free Apps…')}
          </Text>
        </Element>
        {isWeb && (
          <Element
            {...{
              config: {
                position: {
                  top: 38,
                  left: -1350,
                },
                width: 4000,
                height: 70,
                backgroundColor: C('Background / Light / Detail [mobile]'),
              },
            }}
          />
        )}
        <Element
          {...{
            config: {
              position: {
                top: 38,
                right: 0,
                left: 0,
              },
              height: 70,
              width: '100%',
              backgroundColor: C('Background / Light / Detail [mobile]'),
            },
          }}
        >
          <Element
            {...{
              config: {
                position: {
                  bottom: 19,
                },
                height: 30,
                width: 102,
                horizontalCenter: -471,
              },
            }}
          >
            {C.scheme === 'dark' ? <AppleW width={scale(102)} height={scale(30)} /> : <Apple width={scale(102)} height={scale(30)} />}
          </Element>
          <Element
            {...{
              config: {
                position: {
                  bottom: 19,
                },
                height: 30,
                width: 102,
                horizontalCenter: -351,
              },
            }}
          >
            {C.scheme === 'dark' ? <AndroidW width={scale(102)} height={scale(30)} /> : <Android width={scale(102)} height={scale(30)} />}
          </Element>
          <Element
            {...{
              config: {
                position: {
                  bottom: 19,
                },
                height: 30,
                width: 102,
                horizontalCenter: -231,
              },
            }}
          >
            {C.scheme === 'dark' ? <WebW width={scale(102)} height={scale(30)} /> : <Web width={scale(102)} height={scale(30)} />}
          </Element>
          <Element
            {...{
              config: {
                height: '100%',
                width: 140,
                horizontalCenter: -100,
                justifyContent: 'center',
              },
            }}
          >
            <Text
              style={[
                {
                  color: C('Label / Light / Tertiary'),
                },
                font['Subhead 1'],
              ]}
            >
              Smart Factory app
            </Text>
          </Element>

          <Element
            {...{
              config: {
                height: '100%',
                width: 110,
                parentHeight: 70,
                horizontalCenter: 113.5,
                justifyContent: 'center',
              },
            }}
          >
            <Text
              style={[
                {
                  color: C('Label / Light / Tertiary'),
                },
                font['Subhead 1'],
              ]}
            >
              Wallboard app
            </Text>
          </Element>

          <Element
            {...{
              config: {
                position: {
                  bottom: 19,
                },
                height: 30,
                width: 102,
                horizontalCenter: 231,
              },
            }}
          >
            {C.scheme === 'dark' ? <AppleW width={scale(102)} height={scale(30)} /> : <Apple width={scale(102)} height={scale(30)} />}
          </Element>
          <Element
            {...{
              config: {
                position: {
                  bottom: 19,
                },
                height: 30,
                width: 102,
                horizontalCenter: 351,
              },
            }}
          >
            {C.scheme === 'dark' ? <AndroidW width={scale(102)} height={scale(30)} /> : <Android width={scale(102)} height={scale(30)} />}
          </Element>
          <Element
            {...{
              config: {
                position: {
                  bottom: 19,
                },
                height: 30,
                width: 102,
                horizontalCenter: 471,
              },
            }}
          >
            {C.scheme === 'dark' ? <TVW width={scale(102)} height={scale(30)} /> : <TV width={scale(102)} height={scale(30)} />}
          </Element>
        </Element>
      </Element>
    </ElementBox>
  );
};

const AddDevice = connect(
  ({ device, signalR }) => ({
    device,
    signalR,
  }),
  { getAuthInfo, authenticateWalboardDevice, submitActivationDetails }
)(AddDeviceComponent);

const WrapperComponent = (data) => (props) => {
  const { C } = props;
  return (
    <View style={{ flex: 1 }}>
      <ActivateDevice />
      <GridBox
        {...{
          backgroundColor: isPhone ? C('Background / Light / Detail [mobile]') : C('Background / Light / System'),
          headerOffset: false,
          notchOffset: false,
          sliderOffset: false,
        }}
      >
        <AddDevice {...{ ...props, ...data }} />
      </GridBox>
    </View>
  );
};

export default ColorWrapper(WrapperComponent());
